import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { UserManagementService } from '../../user-management/user-management.service';
import { CreateJobCardVoRequest, JobCardDetails, JobCardSearchRequest, ManageComplaintService, UpdateComplaintStatusVo } from '../../../core/services/manage-complaints.service';
import { LookupItem } from '../../../core/services/lookup.service';
import { AuthContext } from '../../../auth/services/auth.context';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { IssueJobCardDialogComponent } from './issue-job-card-dialog/issue-job-card-dialog.component';
import { UpdateComplaintDepartmentDialogComponent } from './update-complaint-department-dialog/update-complaint-department-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ComplaintHistoryDialogComponent } from './complaint-history-dialog/complaint-history-dialog.component';
import { UpdateComplaintStatusDialogComponent } from './update-complaint-status-dialog/update-complaint-status-dialog.component';
import { ReportService } from '../../../core/services/report.service';

@Component({
  selector: 'app-manage-job-cards',
  standalone: true,
  imports: [
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatTableModule,
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatMenuModule,
    FormsModule,
    MatCheckboxModule
  ],
  templateUrl: './manage-job-cards.component.html',
  styleUrl: './manage-job-cards.component.scss'
})
export class ManageJobCardsComponent implements OnInit, AfterViewInit {

  private snackBar = inject(MatSnackBar);

  areas: SimpleCodeValue[] = [];
  departments: SimpleCodeValue[] = [];
  employees: SimpleCodeValue[] = [];
  jobCardStatus: LookupItem[] = [];
  complaintStatus: LookupItem[] = [];

  requestForm = new FormGroup({
    areaId: new FormControl(null),
    departmentId: new FormControl(null),
    employeeId: new FormControl(null),
    jobCardStatus: new FormControl(''),
    complaintStatus: new FormControl(''),
    complaintNumber: new FormControl(''),
    jobCardNumber: new FormControl(''),
  });

  displayedColumns: string[] = [
    'complaintNumber', 'complaintStatus', 'location', 'complaintDescription',
    'complaintDate', 'dueDate', 'taskDeptName', 'jobCardStatus', 'select'
  ];
  selection = new SelectionModel<JobCardDetails>(true, []);
  dataSource: MatTableDataSource<JobCardDetails> = new MatTableDataSource();
  totalItems = 0;  // Total number of items
  limit = 500;      // Number of items per page
  offset = 0;      // Offset (starting index)

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  showActions: boolean = false;
  showResendOtpButton: boolean = false;
  showIssueJobCardButton: boolean = false;
  showIssueAdditionalJobCardButton: boolean = false;
  showJobCardActionButton: boolean = false;
  showTransferComplaintButton: boolean = false;

  showInProgressJobCardAction: boolean = false;
  showOnHoldJobCardAction: boolean = false;
  showCloseJobCardAction: boolean = false;
  showWaitingStoresAction: boolean = false;
  showDiscardAction: boolean = false;
  showApprovalRequiredAction: boolean = false;
  showPrintJobCard: boolean = false;

  readonly dialog = inject(MatDialog);

  showResendOTP: boolean = false;

  showViewHistoryButton: boolean = false;

  recentlyUpdatedRowKeys: Set<string> = new Set();

  isProcessing: boolean = false;
  processingRowKeys: Set<string> = new Set();

  constructor(
    private userManagementService: UserManagementService,
    private cdr: ChangeDetectorRef,
    private manageComplaintService: ManageComplaintService,
    private authContext: AuthContext,
    private reportService: ReportService,
  ) {
  }

  ngOnInit(): void {
    const lookUps = this.authContext.getLookups();
    this.jobCardStatus = lookUps['JOBCARD_STATUS_TYPE'];
    this.complaintStatus = lookUps['COMPLAINT_STATUS_TYPE'];
    this.getUserZoneAreas();
    this.getDepartments();
    this.getEmployees();
    this.onSearch();

    const zoneIds = this.authContext.getUserInfo().zoneIds;
    if (zoneIds.includes(1)) {
      this.showResendOTP = true;
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.cdr.detectChanges();
  }

  getUserZoneAreas() {
    this.userManagementService.getUserAreas().subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  getDepartments() {
    this.manageComplaintService.getMyZoneAssignableDepartments().subscribe({
      next: (response) => {
        this.departments = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  getEmployees() {
    this.manageComplaintService.getEmployeesWithAssignableComplaints().subscribe({
      next: (response) => {
        this.employees = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  onSearch() {
    this.selection.clear();
    this.showActions = false;
    this.showViewHistoryButton = false;
    this.showIssueAdditionalJobCardButton = false;
    // console.log(this.requestForm.value);
    const complaintNumber = this.requestForm.value.complaintNumber?.trim();
    const jobCardNumber = this.requestForm.value.jobCardNumber?.trim();
    let complaintStatus: string[] = [];
    if (this.requestForm.value.complaintStatus) {
      complaintStatus[0] = this.requestForm.value.complaintStatus;
    }
    let jobCardStatus: string[] = [];
    if (this.requestForm.value.jobCardStatus) {
      jobCardStatus[0] = this.requestForm.value.jobCardStatus;
    }
    const searchRequest: JobCardSearchRequest = {
      complaintStatus: complaintStatus,
      jobCardStatus: jobCardStatus,
      departmentId: this.requestForm.value.departmentId,
      employeeId: this.requestForm.value.employeeId,
      areaId: this.requestForm.value.areaId,
      complaintNumber: complaintNumber ? complaintNumber : null,
      jobCardNumber: jobCardNumber ? jobCardNumber : null,
      limit: this.limit,
      offset: this.offset,
    };
    // console.log(searchRequest);
    this.manageComplaintService.getJobCardSearchDetails(searchRequest).subscribe({
      next: (response) => {
        // console.log(response);
        this.dataSource.data = response; // Assuming response.data contains the complaints
        this.totalItems = response.length; // Assuming response.totalItems contains the total number of complaints
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  getJobCardStatusValue(code: string) {
    const lookup = this.jobCardStatus.find(item => item.lookupCode === code);
    return lookup ? lookup.lookupValue : code;
  }

  getComplaintStatusValue(code: string) {
    const lookup = this.complaintStatus.find(item => item.lookupCode === code);
    return lookup ? lookup.lookupValue : code;
  }

  onPaginateChange(event: any) {
    this.limit = 500;
    console.log('event');
    // this.offset = event.pageIndex * event.pageSize;
    // this.onSearch(); // Reload data based on new pagination settings
  }

  onReset() {
    this.requestForm.reset({
      // complaintStatus: 'ASSIGNED'
    });
    this.onSearch(); // Reload data after resetting form
    this.selection.clear();
  }
  
  onCheckboxChange(location: JobCardDetails) {
    if (this.selection.isSelected(location)) {
      this.selection.deselect(location);
    } else {
      this.selection.select(location);
    }
  
    const selectedItems = this.selection.selected;
    console.log(selectedItems);
  
    // Reset all button visibility to default
    const defaultComplaintStatusButtons = {
      showResendOtpButton: false,
      showIssueJobCardButton: false,
      showIssueAdditionalJobCardButton: false,
      showJobCardActionButton: false,
      showTransferComplaintButton: false,
      showPrintJobCard: false,
    };
  
    const defaultJobCardButtons = {
      showInProgressJobCardAction: false,
      showOnHoldJobCardAction: false,
      showCloseJobCardAction: false,
      showWaitingStoresAction: false,
      showDiscardAction: false,
      showApprovalRequiredAction: false,
      showPrintJobCard: false,
    };
  
    // Hide all by default
    Object.assign(this, defaultComplaintStatusButtons, defaultJobCardButtons);
    this.showActions = selectedItems.length > 0;
  
    if (selectedItems.length === 0) {
      this.cdr.detectChanges();
      return;
    }
  
    // Mappings
    const statusButtonMapping = {
      'JOBCARDISSUED': {
        showResendOtpButton: true,
        showIssueJobCardButton: false,
        showIssueAdditionalJobCardButton: true,
        showJobCardActionButton: true,
        showPrintJobCard: true,
      },
      'ASSIGNED': {
        showResendOtpButton: false,
        showIssueJobCardButton: true,
        showIssueAdditionalJobCardButton: false,
        showJobCardActionButton: false,
        showPrintJobCard: false,
      },
      'REOPEN': {
        showResendOtpButton: false,
        showIssueJobCardButton: true,
        showIssueAdditionalJobCardButton: false,
        showJobCardActionButton: false,
        showPrintJobCard: false,
      },
      'CLOSED': {
        showResendOtpButton: false,
        showIssueJobCardButton: false,
        showIssueAdditionalJobCardButton: false,
        showJobCardActionButton: false,
        showPrintJobCard: false,
      },
    };
  
    const jobCardStatusButtonMapping = {
      'WAITING_STORES': {
        showInProgressJobCardAction: true,
        showOnHoldJobCardAction: true,
        showCloseJobCardAction: true,
        showDiscardAction: true,
        showApprovalRequiredAction: true,
        showWaitingStoresAction: false,
        showPrintJobCard: true,
      },
      'HOLD': {
        showInProgressJobCardAction: true,
        showCloseJobCardAction: true,
        showWaitingStoresAction: true,
        showDiscardAction: true,
        showApprovalRequiredAction: true,
        showOnHoldJobCardAction: false,
        showPrintJobCard: true,
      },
      'IN_PROGRESS': {
        showOnHoldJobCardAction: true,
        showCloseJobCardAction: true,
        showWaitingStoresAction: true,
        showDiscardAction: true,
        showApprovalRequiredAction: true,
        showInProgressJobCardAction: false,
        showPrintJobCard: true,
      },
      'DISCARD': {
        showOnHoldJobCardAction: true,
        showInProgressJobCardAction: true,
        showCloseJobCardAction: true,
        showWaitingStoresAction: true,
        showApprovalRequiredAction: true,
        showDiscardAction: false,
        showPrintJobCard: false,
      },
      'APPROVAL_REQUIRED': {
        showOnHoldJobCardAction: true,
        showInProgressJobCardAction: true,
        showCloseJobCardAction: true,
        showWaitingStoresAction: true,
        showDiscardAction: true,
        showApprovalRequiredAction: false,
        showPrintJobCard: true,
      },
    };
  
    const firstItem = selectedItems[0];
  
    // --- COMPLAINT STATUS LOGIC ---
    const complaintStatus = firstItem.complaintStatus as keyof typeof statusButtonMapping;
    const complaintButtons = statusButtonMapping[complaintStatus] || defaultComplaintStatusButtons;
    Object.assign(this, complaintButtons);
  
    // If departments mismatch, hide Issue Job Card
    const firstDeptId = firstItem.compAssignedDeptId;
    const allSameDept = selectedItems.every(item => item.compAssignedDeptId === firstDeptId);
    if (!allSameDept) {
      this.showIssueJobCardButton = false;
    }

    const hasClosedComplaints = selectedItems.every(item => item.complaintStatus === 'CLOSED');
    if (!hasClosedComplaints) {
      this.showPrintJobCard = false;
    }

    // If departments mismatch, hide Issue Job Card
    const firstComplaintStatus = firstItem.complaintStatus;
    const allSameComplaintStatus = selectedItems.every(item => item.complaintStatus === firstComplaintStatus);
    if (!allSameComplaintStatus) {
      this.showPrintJobCard = false;
      this.showResendOtpButton = false;
      this.showIssueJobCardButton = false;
      this.showIssueAdditionalJobCardButton = false;
      this.showJobCardActionButton = false;
    }

    // Transfer button: only show when all selected complaints are ASSIGNED and user role is ADMIN or GE
    const allAssigned = selectedItems.every(item => item.complaintStatus === 'ASSIGNED');
    const userType = this.authContext.getUserInfo().userType;
    if (allAssigned && (userType === 'ROLE_ADMIN' || userType === 'ROLE_AGE')) {
      this.showTransferComplaintButton = true;
    } else {
      this.showTransferComplaintButton = false;
    }
  
    // --- JOBCARD STATUS LOGIC ---
    const taskStatus = firstItem.taskStatus as keyof typeof jobCardStatusButtonMapping;
    const jobCardButtons = jobCardStatusButtonMapping[taskStatus] || defaultJobCardButtons;
    Object.assign(this, jobCardButtons);

    if(selectedItems.length > 1) {
      this.showViewHistoryButton = false;
      this.showIssueAdditionalJobCardButton = false;
    } else {
      this.showViewHistoryButton = true;
    }
  
    this.cdr.detectChanges();
  }

  printJobCardEmpWise(reportName: string) {
    const selectedTaskIds = this.selection.selected
      .map((data: JobCardDetails) => data.taskId.toString())
      .join(',');
    const payload = {
      taskIds: selectedTaskIds
    };
    this.reportService.downloadReport(reportName, payload).subscribe(
      (response: any) => {
        this.downloadPDF(response, reportName);
        this.snackBar.open('Report generated successfully', 'Close', { duration: 3000 });
      },
      (error: any) => {
        console.error('Error generating report:', error);
        this.snackBar.open('Error generating report. Please try again.', 'Close', { duration: 3000 });
      }
    );
  }

  downloadPDF(blob: any, reportName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName}_${new Date().getTime()}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  updateJobCardStatus(status: string) {
    const data: JobCardDetails[] = this.selection.selected;
    const dialogRef = this.dialog.open(UpdateComplaintStatusDialogComponent, {
      data: {
        jobCardData: data,
        title: 'Update Job Card Status for ' + 'Complaint No.s: ' + data.map(item => item.complaintId).join(', '),
        status: status
      },
      width: '80%',
      height: '80%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'full-screen-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.okay) {
        this.setProcessingState(data);
        const updateJobCardStatusReq: UpdateComplaintStatusVo = {
          jobCardSearchResultList: data,
          additionalComment: result.additionalComments,
          jobCardComments: result.userComments,
          newStatus: status
        }
        this.manageComplaintService.updateComplaintStatus(updateJobCardStatusReq).subscribe({
          next: (response) => {
            if (response.valid) {
              this.openSnackBar(response.message, 'Okay');
              this.refreshPreservingState(data);
            } else {
              this.clearProcessingState();
              this.openSnackBar(response.message, 'Okay');
            }
          },
          error: (error) => {
            this.clearProcessingState();
            console.error('POST error:', error);
          },
        });
      }
    });
  }

  viewComplaintHistory() {
    const data: JobCardDetails = this.selection.selected[0];
    this.manageComplaintService.getComplaintHistory(data.complaintLineId).subscribe({
      next: (response) => {
        // console.log(response);
        const complaintHistory = response;
        const dialogRef = this.dialog.open(ComplaintHistoryDialogComponent, {
          data: {
            jobCardData: data,
            title: 'Complaint History: ' + data.complaintId,
            history: complaintHistory
          },
          width: '95%',
          height: '95%',
          maxWidth: '100%',
          maxHeight: '100%',
          panelClass: 'full-screen-dialog'
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result.okay) {
            // Something
          } else {
            // Something
          }
        });
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  issueJobCard() {
    const data: JobCardDetails[] = this.selection.selected;
    const dialogRef = this.dialog.open(IssueJobCardDialogComponent, {
      data: {
        jobCardData: data,
        title: 'Complaint No: ' + data.map(item => item.complaintId?.toString()).join(', '),
        departments: this.departments
      },
      width: '80%',
      height: '80%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'full-screen-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.approved) {
        if (result.employeeId) {
          const jobCardRequest: CreateJobCardVoRequest = {
            jobCardSearchResultList: result.jobCardData,
            employeeIdForIssue: Number(result.employeeId),
            departmentIdForIssue: result.departmentId,
            jobCardComments: result.comments
          };
          this.setProcessingState(result.jobCardData);
          this.manageComplaintService.saveJobCard(jobCardRequest).subscribe({
            next: (response) => {
              if (response.valid) {
                this.openSnackBar(response.message, 'Okay');
                this.refreshPreservingState(result.jobCardData);
              } else {
                this.clearProcessingState();
                this.openSnackBar(response.message, 'Okay');
              }
            },
            error: (error) => {
              this.clearProcessingState();
              console.error('POST error:', error);
            },
          });
        }
      }
    });
  }

  transferComplaint() {
    const data: JobCardDetails[] = this.selection.selected;
    const dialogRef = this.dialog.open(UpdateComplaintDepartmentDialogComponent, {
      data: {
        jobCardData: data,
        title: 'Transfer Complaint: ' + data.map(item => item.complaintId?.toString()).join(', ')
      },
      width: '60%',
      height: '60%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'full-screen-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.approved) {
        const body = {
          complaintLineIds: data.map(item => item.complaintLineId),
          departmentId: Number(result.departmentId)
        };
        this.setProcessingState(data);
        this.manageComplaintService.transferComplaint(body).subscribe({
          next: (response: any) => {
            if (response.valid) {
              this.openSnackBar(response.message, 'Okay');
              this.refreshPreservingState(data);
            } else {
              this.clearProcessingState();
              this.openSnackBar(response.message, 'Okay');
            }
          },
          error: (error) => {
            this.clearProcessingState();
            console.error('POST error:', error);
          }
        });
      }
    });
  }

  resendOtp() {
    const resendOtpRequest = this.selection.selected[0];
    this.manageComplaintService.resentOtpForJobClose(resendOtpRequest).subscribe({
      next: (response) => {
        // console.log(response);
        if (response.valid) {
          this.openSnackBar(response.message, 'Okay');
        } else {
          this.openSnackBar(response.message, 'Okay');
        }
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }

  /** Builds a search request from the current form filters */
  private buildCurrentSearchRequest(): JobCardSearchRequest {
    const complaintNumber = this.requestForm.value.complaintNumber?.trim();
    const jobCardNumber = this.requestForm.value.jobCardNumber?.trim();
    let complaintStatus: string[] = [];
    if (this.requestForm.value.complaintStatus) {
      complaintStatus[0] = this.requestForm.value.complaintStatus;
    }
    let jobCardStatus: string[] = [];
    if (this.requestForm.value.jobCardStatus) {
      jobCardStatus[0] = this.requestForm.value.jobCardStatus;
    }
    return {
      complaintStatus: complaintStatus,
      jobCardStatus: jobCardStatus,
      departmentId: this.requestForm.value.departmentId,
      employeeId: this.requestForm.value.employeeId,
      areaId: this.requestForm.value.areaId,
      complaintNumber: complaintNumber ? complaintNumber : null,
      jobCardNumber: jobCardNumber ? jobCardNumber : null,
      limit: this.limit,
      offset: this.offset,
    };
  }

  /** Re-search with current filters, preserving pagination state */
  private refreshPreservingState(updatedItemsToHighlight: JobCardDetails[] = []) {
    const currentPageIndex = this.paginator?.pageIndex ?? 0;
    const currentPageSize = this.paginator?.pageSize ?? 10;

    this.selection.clear();
    this.showActions = false;
    this.showViewHistoryButton = false;
    this.showIssueAdditionalJobCardButton = false;
    this.showTransferComplaintButton = false;

    const searchRequest = this.buildCurrentSearchRequest();
    this.manageComplaintService.getJobCardSearchDetails(searchRequest).subscribe({
      next: (response) => {
        this.dataSource.data = response;
        this.totalItems = response.length;

        if (updatedItemsToHighlight.length > 0) {
          const selectedRowKeys = new Set(updatedItemsToHighlight.map(item => this.getRowKey(item)));
          const selectedLineKeyPairs = new Set(updatedItemsToHighlight.map(item => `${item.complaintId}-${item.complaintLineId}`));

          // First preference: strict match to exact originally selected rows.
          let matchedRows = response.filter(row => selectedRowKeys.has(this.getRowKey(row)));

          // Fallback for flows where taskId can be regenerated after update.
          if (matchedRows.length === 0) {
            matchedRows = response.filter(row => selectedLineKeyPairs.has(`${row.complaintId}-${row.complaintLineId}`));
          }

          const matchedUpdatedRowKeys = new Set(matchedRows.map(row => this.getRowKey(row)));

          this.setRowHighlight(matchedUpdatedRowKeys);
        }

        this.clearProcessingState();

        // Restore pagination state after data loads
        setTimeout(() => {
          if (this.paginator) {
            this.paginator.pageIndex = currentPageIndex;
            this.paginator.pageSize = currentPageSize;
            this.paginator.page.emit({
              pageIndex: currentPageIndex,
              pageSize: currentPageSize,
              length: this.totalItems
            });
          }
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.clearProcessingState();
        console.error('POST error:', error);
      },
    });
  }

  isRecentlyUpdated(element: JobCardDetails): boolean {
    return this.recentlyUpdatedRowKeys.has(this.getRowKey(element));
  }

  private getRowKey(item: JobCardDetails): string {
    return `${item.complaintId}-${item.complaintLineId}-${item.taskId}`;
  }

  private setRowHighlight(rowKeys: Set<string>) {
    this.recentlyUpdatedRowKeys = rowKeys;
    setTimeout(() => {
      this.recentlyUpdatedRowKeys = new Set();
      this.cdr.detectChanges();
    }, 3000);
  }

  private setProcessingState(items: JobCardDetails[]) {
    this.isProcessing = true;
    this.processingRowKeys = new Set(items.map(item => this.getRowKey(item)));
    this.cdr.detectChanges();
  }

  private clearProcessingState() {
    this.isProcessing = false;
    this.processingRowKeys = new Set();
    this.cdr.detectChanges();
  }

  isRowProcessing(element: JobCardDetails): boolean {
    return this.processingRowKeys.has(this.getRowKey(element));
  }

}
